import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, dateTimeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, PenNib, SealCheck, Warning } from "@phosphor-icons/react";

const STATE = {
  signed: ["Signed", "bg-emerald-50 text-emerald-700", SealCheck],
  resign_needed: ["Updated — sign again", "bg-amber-50 text-amber-700", Warning],
  unsigned: ["Needs your signature", "bg-red-50 text-red-600", PenNib],
};

export default function ParentForms() {
  const [forms, setForms] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/forms/status").then(({ data }) => setForms(data)).catch((e) => setError(apiError(e.response?.data?.detail)));
  }, []);

  const todo = (forms || []).reduce((n, f) => n + f.children.filter((c) => c.status !== "signed").length, 0);

  return (
    <div data-testid="parent-forms">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Paperwork</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Forms & signatures</h1>
      <p className="mt-3 text-base text-muted-foreground">
        Sign online in a minute — type your legal name and check the box. Signed copies stay in your child's records.
      </p>
      {forms && <p className="mt-3 text-sm font-bold" data-testid="forms-todo">{todo === 0 ? "Everything is signed. Thank you!" : `${todo} signature${todo === 1 ? "" : "s"} needed`}</p>}
      {error && <p className="mt-4 text-[#EF4444] font-semibold">{error}</p>}

      <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="parent-forms-list">
        {forms && forms.length === 0 && <p className="text-muted-foreground">Nothing to complete right now. We'll let you know.</p>}
        {(forms || []).map((f) => (
          <div key={f.id} className="bg-white rounded-2xl border border-border p-6" data-testid={`parent-form-${f.id}`}>
            <div className="flex items-start gap-4">
              <span className="h-12 w-12 rounded-2xl bg-blue-50 text-[#3B82F6] grid place-items-center shrink-0"><FileText size={24} weight="duotone" /></span>
              <div className="min-w-0">
                <p className="font-display text-lg font-semibold">{f.title}</p>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6] mt-1">{f.kind} · v{f.version}</p>
                {f.description && <p className="mt-2 text-sm text-muted-foreground">{f.description}</p>}
                {f.due_date && <p className="mt-2 text-xs font-bold text-[#EF4444]">Due {f.due_date}</p>}
              </div>
            </div>
            <div className="mt-5 space-y-3">
              {f.children.map((c) => {
                const [label, tone, Icon] = STATE[c.status];
                return (
                  <div key={c.student_id} className="rounded-2xl border border-border p-4 flex flex-wrap items-center gap-3" data-testid={`form-${f.id}-child-${c.student_id}`}>
                    <div className="flex-1 min-w-[10rem]">
                      <p className="font-bold text-base">{c.student_name}</p>
                      <span className={`inline-flex items-center gap-1 mt-1 text-xs font-bold px-2.5 py-1 rounded-full ${tone}`} data-testid={`form-status-${f.id}-${c.student_id}`}>
                        <Icon size={12} /> {label}{c.status === "signed" && c.signed_at ? ` · ${dateTimeOf(c.signed_at)}` : ""}{c.kind === "paper" ? " (paper copy)" : ""}
                      </span>
                    </div>
                    {c.status === "signed" ? (
                      <Link to={`/portal/signed/${c.signature_id}`}><Button size="sm" variant="outline" className="rounded-full font-bold h-11" data-testid={`view-signed-${f.id}-${c.student_id}`}>View signed copy</Button></Link>
                    ) : (
                      <Link to={`/portal/forms/${f.id}/sign?student=${c.student_id}`}><Button size="sm" className="rounded-full font-bold h-11 bg-[#3B82F6] hover:bg-[#2563EB]" data-testid={`sign-now-${f.id}-${c.student_id}`}><PenNib size={16} className="mr-1" /> Sign now</Button></Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
